import { z } from "zod";

/**
 * Club feature flags. Stored as a nested `features` map inside the
 * `clubs/{clubId}` document (not its own subcollection).
 */

export const NotificationPreferencesSchema = z.object({
  /** Push when a booking is created (any source). */
  pushOnNewBooking: z.boolean().default(true),
  /** Push when a booking is cancelled. */
  pushOnCancellation: z.boolean().default(true),
  /** Extra push specifically for online-source bookings. */
  pushOnOnlineBooking: z.boolean().default(true),
});

export type NotificationPreferences = z.infer<
  typeof NotificationPreferencesSchema
>;

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  pushOnNewBooking: true,
  pushOnCancellation: true,
  pushOnOnlineBooking: true,
};

// ── Multi-channel notification preferences ──────────────────────────────────
// Shape: `features.notifications.{channel}.{recipient}.{event}: boolean`.
//
//   channel    e.g. `email`, future: `push`, `sms`, `whatsapp`
//   recipient  e.g. `players`, `owner`, future: `staff`, `instructor`
//   event      e.g. `bookingConfirmation`, future: `bookingArrived`,
//              `paymentReceived`, `weeklyDigest`, ...
//
// Each channel sub-schema is an explicit object (typed keys, type-safe
// reads in code) but the file is structured so adding a new event = add
// one line to the matching channel/recipient object. The CF gate
// (`features.notifications.email.players.bookingConfirmation`) follows
// the same path verbatim — no string-key duplication beyond the schema.

/**
 * Per-event email toggles, scoped by recipient. Defaults OFF so existing
 * clubs (and freshly-claimed previews) don't start sending mail in the
 * owner's name without explicit opt-in.
 */
export const EmailNotificationsSchema = z.object({
  players: z.object({
    /** Booking confirmation sent to the player on creation. */
    bookingConfirmation: z.boolean().default(false),
    /** Cancellation notice sent to the player. */
    bookingCancellation: z.boolean().default(false),
    /** Reschedule notice (time / court / duration change). */
    bookingRescheduled: z.boolean().default(false),
  }),
  owner: z.object({
    /** One email per booking created (any source). */
    bookingCreated: z.boolean().default(false),
    /** End-of-day operations summary at 22:00 Madrid. */
    dailySummary: z.boolean().default(false),
  }),
});

export type EmailNotifications = z.infer<typeof EmailNotificationsSchema>;

export const DEFAULT_EMAIL_NOTIFICATIONS: EmailNotifications = {
  players: {
    bookingConfirmation: false,
    bookingCancellation: false,
    bookingRescheduled: false,
  },
  owner: {
    bookingCreated: false,
    dailySummary: false,
  },
};

/**
 * Top-level notifications namespace. One sub-key per channel; add new
 * channels (push, sms, whatsapp) by appending a matching field here
 * with its own `{Channel}NotificationsSchema`. The Settings UI groups
 * by channel and renders one panel per (channel × recipient) pair.
 */
export const NotificationsSchema = z.object({
  email: EmailNotificationsSchema.default(DEFAULT_EMAIL_NOTIFICATIONS),
});

export type Notifications = z.infer<typeof NotificationsSchema>;

export const DEFAULT_NOTIFICATIONS: Notifications = {
  email: DEFAULT_EMAIL_NOTIFICATIONS,
};

export const ClubFeaturesSchema = z.object({
  /**
   * The club is LIVE IN THE PLAYER APP (usersmatch): its listing is fed from
   * here, and players find it and request courts from there.
   *
   * NOT a booking page on the club's own site. `book.upadelmatch.com/{slug}`
   * — which this comment used to promise — is never going to exist: online
   * booking IS the player app (founder decision 2026-08-10). A per-club QR for
   * the door is the physical entry point into it.
   *
   * Defaults to FALSE: an owner opts in, because switching it on is a
   * declaration that this club takes reservations through us, and that
   * declaration is what links their directory row (spec/usersmatch/01).
   */
  onlineBookings: z.boolean().default(false),
  /**
   * WHAT the linked club shows players. Separate switches on purpose: agreeing
   * to appear in the player app is a different decision from publishing your
   * mobile number, and conflating them means a club that wants to be findable
   * has to hand over a phone it never meant to publish.
   *
   * Each one simply decides whether the projection sends that field —
   * usersmatch already renders the phone and WhatsApp buttons independently,
   * each hidden when its field is absent.
   *
   * Both default FALSE (founder call, 2026-08-10, reversing the first cut).
   *
   * The first version defaulted TRUE, reasoning that a club which just opted
   * in wants to be reachable. That contradicted the feature's own thesis: if
   * enabling the module publishes your mobile anyway, the two decisions are
   * only separated on paper — the observable behaviour is identical to the
   * single switch this replaced.
   *
   * The decider is that the risks are not symmetric. Default hidden and a club
   * turns it on: mild friction, one tap. Default visible and they never wanted
   * it: the owner's personal mobile is public and players are already
   * messaging it — and you cannot un-send those. `whatsapp` is usually a
   * MOBILE (a landline cannot receive WhatsApp), so it is frequently the
   * owner's own number.
   *
   * The cost is real and handled elsewhere rather than ignored: a club that
   * opts in and publishes nothing is LISTED BUT UNREACHABLE, which is exactly
   * what the 2026-08-10 WhatsApp backfill existed to fix. So that state is
   * surfaced loudly (see `player-app-visibility-panel` and the contact panel
   * summary) instead of being a silent default nobody notices.
   *
   * ⚠️ This default is declared in FOUR places that must agree — here,
   * DEFAULT_CLUB_FEATURES below, `places_projection.py` (both the payload and
   * the SIGNATURE), and the panel's `??` fallback. A mismatch is the worst
   * outcome available: the UI reads "oculto" while the projection publishes.
   */
  showPhoneToPlayers: z.boolean().default(false),
  showWhatsappToPlayers: z.boolean().default(false),
  /** Show the CRM / Jugadores tab. Records are always auto-created. */
  crmEnabled: z.boolean().default(true),
  /**
   * Multi-channel notification preferences. Replaces the previous
   * single `emailConfirmations` boolean — owners can now silence
   * individual emails per recipient + event.
   */
  notifications: NotificationsSchema.default(DEFAULT_NOTIFICATIONS),
  /** Push notifications to staff on booking events. */
  pushNotifications: z.boolean().default(true),
  /** Track no-shows per player in CRM. */
  noShowTracking: z.boolean().default(true),
  notificationPreferences: NotificationPreferencesSchema.default(
    DEFAULT_NOTIFICATION_PREFERENCES,
  ),
  /**
   * Owner opt-in for player credit (Monedero + Bonos), and the SOLE live gate
   * as of the v1.26.0 self-serve GA. Off by default; the owner switches it on
   * in Ajustes and the Monedero goes live — no platform/admin step.
   * Spec: `docs/product/spec/clubmanager/18-credit-monedero.md` (BR-1/BR-2).
   */
  walletEnabled: z.boolean().default(false),
  /**
   * Owner opt-in for the Coach / Entrenador role, and the SOLE live gate as of
   * the v1.26.0 self-serve GA. Off by default; the owner switches it on in
   * Ajustes and the role's surfaces (Entrenador invite option, coach agenda
   * lens, per-coach report) go live — no platform/admin step. See `coachRoleLive`.
   */
  coachRoleEnabled: z.boolean().default(false),
  /**
   * Owner opt-in for Torneos (tournaments/leagues), the SOLE live gate (self-
   * serve GA, no platform/admin step). Off by default; the owner switches it on
   * in Ajustes and the Torneos section + nav entry go live. See `tournamentsLive`.
   * Spec: `docs/product/spec/clubmanager/19-tournaments.md`.
   */
  tournamentsEnabled: z.boolean().default(false),
  /**
   * Owner opt-in for the POS / Ventas module (the product catalogue + quick-sale
   * grid — selling balls, grip, rentals, drinks…) as a **club Módulo**
   * (`docs/PLAN-clubmanager-pwa.md` M1). This is distinct from Cobrar (collecting
   * a booking's own payment) and Ingresos (the read-only ledger) — both of those
   * stay core and are never gated. `posLive` is the sole gate. Defaults `true`
   * (default-on module) — every club uses Ventas out of the box; the owner can
   * still switch it off. This increment (M1) ships the
   * flag + the Módulos catalog/toggle only — the Ventas route/nav itself is NOT
   * yet gated on it (tracked as the next increment).
   */
  posEnabled: z.boolean().default(true),
  /**
   * Owner opt-in for the Series (recurring bookings) module as a **club
   * Módulo** (`docs/PLAN-clubmanager-pwa.md` M1). `seriesLive` is the sole
   * gate. Defaults `true` (default-on module) so recurring bookings stay
   * visible out of the box. This increment ships the flag + the Módulos
   * catalog/toggle only — the Series route/nav itself is NOT yet gated on it
   * (tracked as the next increment).
   */
  seriesEnabled: z.boolean().default(true),
});

export type ClubFeatures = z.infer<typeof ClubFeaturesSchema>;

export const DEFAULT_CLUB_FEATURES: ClubFeatures = {
  onlineBookings: false,
  showPhoneToPlayers: false,
  showWhatsappToPlayers: false,
  crmEnabled: true,
  notifications: DEFAULT_NOTIFICATIONS,
  pushNotifications: true,
  noShowTracking: true,
  notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
  walletEnabled: false,
  coachRoleEnabled: false,
  tournamentsEnabled: false,
  posEnabled: true,
  seriesEnabled: true,
};
